import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import FloatingTooltip from "../../../Utility/FloatingTooltip/FloatingTooltip";
import NoDataFound from "../../../Utility/NoDataFound/NoDataFound";
import { ContentLoader } from "../../../Utility/Loader";
import { useSorting, SortableHeader } from "../../../Utility/Sorting";
import { RiPencilLine, RiEyeLine, RiDeleteBinFill } from "react-icons/ri";
import AddRole from "./AddRole";
import EditRole from "./EditRole";
import DeleteConfirmModal from "../../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import { fetchRolesData, deleteRole } from "../../../Redux/Thunks/rolesThunk";
import { selectRolesList, selectRolesLoading } from "../../../Redux/Slices/rolesSlice";
import AddButton from "../../../Utility/AddButton/AddButton";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../../permissions/permissionKeys";

const RoleTab = ({ onViewRolePermissions }) => {
  const { can } = usePermissions();
  const canManageRoles = can(PERMISSION_KEYS.SETTINGS_ROLE_PERMISSION);
  const dispatch = useDispatch();
  const roles = useSelector(selectRolesList);
  const loading = useSelector(selectRolesLoading);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchRolesData());
  }, [dispatch]);

  const handleAddRoleSuccess = () => {
    dispatch(fetchRolesData());
  };

  const handleConfirmDeleteRole = async () => {
    if (!roleToDelete?.id) return;
    const id = roleToDelete.id;
    setRoleToDelete(null);
    try {
      await dispatch(deleteRole(id)).unwrap();
      dispatch(fetchRolesData());
    } catch {
      /* errors surfaced via toast from thunk */
    }
  };

  const handleViewRole = (role) => {
    onViewRolePermissions?.(role);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setIsEditModalOpen(true);
  };

  const handleEditRoleSuccess = () => {
    dispatch(fetchRolesData());
  };

  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(roles, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  return (
    <>
      <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Typography variant="text1" className="font-semibold text-text-primary">
              Roles
            </Typography>
            <Typography variant="text2" className="text-text-secondary">
              Define roles for your organization
            </Typography>
          </div>
          {canManageRoles ? (
            <AddButton onClick={() => setIsAddModalOpen(true)} tooltip="Add Role" />
          ) : null}
        </div>

        <div className="w-full relative flex flex-col min-w-0 bg-surface-primary rounded-md shadow-sm transition-shadow">
          <div className="flex-1 p-2">
            <div className="live-preview">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse mb-0 clients-table">
                  <thead className="bg-surface-secondary">
                    <tr>
                      <SortableHeader
                        className="content-cell"
                        columnKey="name"
                        label="Role Name"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        className="action-col"
                        columnKey="action"
                        label="Action"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        sortable={false}
                      />
                    </tr>
                  </thead>
                  <tbody className="relative">
                    {loading ? (
                      <tr aria-hidden="true">
                        <td colSpan={2} className="min-h-[240px] h-60 p-0 border-0" />
                      </tr>
                    ) : sortedData.length > 0 ? (
                      sortedData.map((role) => (
                        <tr key={role.id}>
                          <td className="text-start align-middle">
                            <Typography variant="span1" className="font-medium">
                              {role.name}
                            </Typography>
                          </td>
                          <td className="text-start align-middle">
                            <div className="flex gap-0.5 items-center justify-end gap-2">
                              <FloatingTooltip label="Edit">
                                <Button
                                  onClick={() => handleEditRole(role)}
                                  aria-label="Edit role"
                                  size="sm"
                                  className="btn-warning-new !w-7 !h-7 !p-0 !font-normal !leading-none flex items-center justify-center border-0"
                                >
                                  <RiPencilLine className="text-white text-[12px]" />
                                </Button>
                              </FloatingTooltip>

                              <FloatingTooltip label="View">
                                <Button
                                  type="button"
                                  onClick={() => handleViewRole(role)}
                                  aria-label="View role"
                                  size="sm"
                                  className="btn-info-new !w-7 !h-7 !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                >
                                  <RiEyeLine className="text-white text-[12px]" />
                                </Button>
                              </FloatingTooltip>

                              <FloatingTooltip label="Delete">
                                <Button
                                  onClick={() => setRoleToDelete(role)}
                                  aria-label="Delete role"
                                  size="sm"
                                  className="btn-danger-new !w-7 !h-7 !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                >
                                  <RiDeleteBinFill className="text-white text-[12px]" />
                                </Button>
                              </FloatingTooltip>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} className="text-center">
                          <NoDataFound message="No roles available. Click 'Add Role' to create one." />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </ContentLoader>

      <AddRole
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddRoleSuccess}
      />

      <EditRole
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedRole(null);
        }}
        onSuccess={handleEditRoleSuccess}
        role={selectedRole}
      />

      <DeleteConfirmModal
        isOpen={!!roleToDelete}
        onClose={() => setRoleToDelete(null)}
        onConfirm={handleConfirmDeleteRole}
        title="Delete role"
        itemName={roleToDelete?.name}
      />
    </>
  );
};

export default RoleTab;
